/**
 * permissions/registry.js — THE single source of truth for admin-panel permissions.
 *
 * Structure mirrors the dashboard exactly:
 *   SECTIONS = الأزرار الرئيسية في لوحة التحكم  → gate "الدخول" (platform_access[section.key])
 *   pages    = الأزرار/الصفحات الفرعية          → gate عرض / تعديل / حذف (permissions[page.id])
 *
 * page.id = route path (or `route#tab` for tabbed sub-pages). Dynamic routes keep their pattern.
 * Flags merged at runtime from registry.generated.json (scripts/gen_permissions_registry.py):
 *   exists (route still in App.js), linked (reachable from the dashboard), caps {edit, delete}.
 * `linked: true` on a node = linked through code the scanner can't follow (tabs / template strings).
 *
 * MAINTENANCE RULE: every time a page is added or removed in the app, update this file
 * and re-run `python3 scripts/gen_permissions_registry.py`.
 */
import generated from './registry.generated.json';

const P = (id, ar, en, extra = {}) => ({ id, ar, en, ...extra });

export const SECTIONS = [
  // ───────────── الإشراف (بطاقات المنصات في الصفحة الرئيسية) ─────────────
  {
    key: 'sup.fzlc', ar: 'CLOUDGM FZ LLC (الإشراف)', en: 'CLOUDGM FZ LLC (Supervision)', group: 'supervision',
    pages: [
      P('/manage/cloudgm', 'منصة العملاء (الإشراف)', 'Customers Platform (Supervision)', { children: [
        P('/manage/cloudgm#cloudgm-users', 'العملاء المسجلون', 'Registered Customers', { linked: true, children: [
          P('/manage-platform/cgc-users/:userId', 'ملف العميل', 'Customer File', { linked: true }),
        ] }),
        P('/manage/cloudgm#services-categories', 'طلبات الخدمات', 'Service Requests', { linked: true, children: [
          P('/control/cloudgm/service-requests/printing-docs', 'الطباعة وتخليص المستندات', 'Printing & Document Clearing', { linked: true }),
          P('/control/cloudgm/service-requests/:serviceType', 'طلبات خدمة (حسب النوع)', 'Service Requests (by type)', { linked: true, children: [
            P('/control/cloudgm/service-requests/:serviceType/requests', 'قائمة الطلبات', 'Requests List', { linked: true }),
            P('/control/cloudgm/service-requests/:serviceType/archives', 'أرشيف الطلبات', 'Requests Archive', { linked: true, children: [
              P('/control/cloudgm/service-requests/:serviceType/archives/:reviewStatus', 'أرشيف حسب الحالة', 'Archive by Status', { linked: true }),
            ] }),
          ] }),
          P('/dashboard/cloudgm/assigned-tasks', 'إضافة طلب لعميل / مهام', 'Assigned Tasks Hub', { linked: true, children: [
            P('/dashboard/cloudgm/assigned-tasks/new', 'إدراج مهمة / طلب جديد', 'New Assigned Task', { linked: true }),
            P('/dashboard/cloudgm/assigned-tasks/list', 'قائمة المهام', 'Assigned Tasks List', { linked: true }),
            P('/dashboard/cloudgm/assigned-tasks/:id', 'تفاصيل المهمة', 'Task Detail', { linked: true }),
          ] }),
          P('/dashboard/cloudgm/legal-translation', 'خدمات الترجمة القانونية (AI)', 'Legal Translation (AI)', { linked: true, children: [
            P('/dashboard/cloudgm/legal-translation/new', 'طلب ترجمة جديد', 'New Translation Request', { linked: true }),
          ] }),
        ] }),
        P('/manage/cloudgm#market-follow', 'متابعة السوق - الحسابات الشخصية', 'Market Follow - Personal Accounts', { linked: true, children: [
          P('/manage/cloudgm#market-cars', 'السيارات', 'Cars', { linked: true }),
          P('/manage/cloudgm#market-car-plates', 'لوحات السيارات', 'Car Plates', { linked: true }),
          P('/manage/cloudgm#market-phone-numbers', 'أرقام الهواتف', 'Phone Numbers', { linked: true }),
        ] }),
      ] }),
      P('/manage/cloudgm-business', 'منصة التجار (الإشراف)', 'Merchants Platform (Supervision)', { children: [
        P('/manage/cloudgm-business#store-requests', 'طلبات تفعيل المتاجر', 'Store Activation Requests', { linked: true }),
        P('/manage/cloudgm-business#stores', 'إدارة المتاجر', 'Stores Management', { linked: true }),
        P('/manage/cloudgm-business#agents-management', 'الوكلاء', 'Agents', { linked: true, children: [
          P('/manage/cloudgm-business#agent-tasks', 'مهام الوكلاء', 'Agent Tasks', { linked: true }),
          P('/manage/cloudgm-business/agent-tasks/new', 'إدراج مهمة وكيل', 'New Agent Task', { linked: true }),
          P('/manage/cloudgm-business/agent-tasks/edit/:id', 'تعديل مهمة وكيل', 'Edit Agent Task', { linked: true }),
        ] }),
        P('/manage/cloudgm-business#listings-management', 'إدارة المعروضات', 'Listings Management', { linked: true }),
        P('/manage/cloudgm-business#order-tracking', 'تتبّع الطلبات', 'Order Tracking', { linked: true }),
        P('/manage/cloudgm-business#accounts', 'الحسابات', 'Accounts', { linked: true, children: [
          P('/manage-platform/merchant-users/:userId', 'ملف التاجر', 'Merchant File', { linked: true }),
        ] }),
        P('/manage/cloudgm-business#subscriptions', 'الاشتراكات', 'Subscriptions', { linked: true }),
        P('/manage/cloudgm-business#support-tickets', 'تذاكر الدعم', 'Support Tickets', { linked: true }),
      ] }),
      P('/manage/cloudgm-fz-llc', 'إدارة شركة CLOUDGM FZ LLC', 'CLOUDGM FZ LLC Management', { children: [
        P('/manage/cloudgm-agent', 'الشركات المسجلة من Cloud GM وشركات الباقات', 'Cloud GM Company Agent', { children: [
          P('/manage/cloudgm-agent/company-registration', 'تسجيل الشركات', 'Company Registration', { children: [
            P('/manage/cloudgm-agent/company-registration/new', 'تسجيل شركة جديدة', 'Register New Company'),
            P('/manage/cloudgm-agent/company-registration/track', 'متابعة تسجيل الشركات', 'Track Registrations', { children: [
              P('/manage/cloudgm-agent/company-registration/track/:id', 'تفاصيل طلب التسجيل', 'Registration Detail', { linked: true }),
            ] }),
          ] }),
          P('/manage/cloudgm-agent/companies', 'ملف الشركات', 'Companies File', { children: [
            P('/manage/cloudgm-agent/companies/:id', 'ملف شركة', 'Company File', { linked: true }),
            P('/manage/cloudgm-agent/companies/:id/edit', 'تعديل ملف شركة', 'Edit Company', { linked: true }),
            P('/manage/cloudgm-agent/company/new', 'إضافة شركة', 'Add Company', { linked: true }),
            P('/manage/cloudgm-agent/residency/new', 'إضافة إقامة/تأشيرة', 'Add Residency/Visa', { linked: true }),
            P('/manage/cloudgm-agent/other-docs', 'الوثائق الأخرى', 'Other Documents', { linked: true }),
            P('/manage/cloudgm-agent/companies/:companyId/renew-license', 'تجديد الرخصة', 'Renew License', { linked: true }),
            P('/manage/cloudgm-agent/companies/:companyId/renewals/:renewalId', 'تفاصيل التجديد', 'Renewal Detail', { linked: true }),
          ] }),
          P('/registered-companies-services', 'خدمات الشركات المسجلة', 'Registered Companies Services', { children: [
            P('/registered-companies-services/new', 'إدراج خدمة لشركة مسجلة', 'Insert Company Service Request'),
            P('/registered-companies-services/tracking', 'متابعة الطلبات', 'Order Tracking & Pipeline'),
            P('/registered-companies-services/:id', 'تفاصيل طلب الخدمة', 'Service Request Detail', { linked: true }),
          ] }),
          P('/manage/cloudgm-agent/commission-audit', 'التدقيق على العمولة المحصلة', 'Commission Collection Audit', { children: [
            P('/manage/cloudgm-agent/commission-audit/:subject_type/:subject_id', 'تفاصيل تدقيق العمولة', 'Commission Audit Detail', { linked: true }),
          ] }),
        ] }),
        P('/manage/cloudgm-fz-llc/certificates', 'شهادات لمن يهمه الأمر والتهنئة', 'Certificates', { children: [
          P('/manage/cloudgm-fz-llc/certificates/:category', 'قائمة شهادات الفئة', 'Category Certificates', { linked: true }),
          P('/manage/cloudgm-fz-llc/certificates/:category/create', 'إنشاء شهادة', 'Create Certificate', { linked: true }),
          P('/manage/cloudgm-fz-llc/certificates/:category/view/:id', 'عرض شهادة', 'View Certificate', { linked: true }),
        ] }),
        P('/manage/cloudgm-fz-llc/quotations', 'عروض الأسعار', 'Price Quotations', { children: [
          P('/manage/cloudgm-fz-llc/quotations/create', 'إنشاء عرض أسعار', 'Create Quotation'),
          P('/manage/cloudgm-fz-llc/quotations/list', 'متابعة عروض الأسعار', 'Manage Quotations'),
          P('/manage/cloudgm-fz-llc/quotations/edit/:id', 'تعديل عرض أسعار', 'Edit Quotation', { linked: true }),
        ] }),
        P('/manage/cloudgm-fz-llc/blank-form', 'فورم الشركة', 'Company Blank Form'),
      ] }),
    ],
  },
  { key: 'sup.cgm-coin', ar: 'CGM COIN (الإشراف)', en: 'CGM COIN (Supervision)', group: 'supervision', comingSoon: true, pages: [] },
  {
    key: 'sup.606-fitness', ar: '606 FITNESS (الإشراف)', en: '606 FITNESS (Supervision)', group: 'supervision',
    pages: [
      P('/control/f606', 'لوحة إشراف 606 Fitness', '606 Fitness Supervision', { children: [
        P('/control/f606/users', 'الحسابات', 'Users', { linked: true }),
        P('/control/f606/trainers', 'المدربون', 'Trainers', { linked: true }),
        P('/control/f606/trainees', 'المتدربون', 'Trainees', { linked: true }),
        P('/control/f606/programs', 'البرامج', 'Programs', { linked: true }),
        P('/control/f606/products', 'المتجر', 'Products', { linked: true }),
        P('/control/f606/orders', 'الطلبات', 'Orders', { linked: true }),
        P('/control/f606/subscriptions', 'الاشتراكات', 'Subscriptions', { linked: true }),
        P('/control/f606/challenges', 'التحديات', 'Challenges', { linked: true }),
        P('/control/f606/clubs', 'الأندية', 'Clubs', { linked: true }),
        P('/control/f606/announcements', 'الإعلانات', 'Announcements', { linked: true }),
        P('/control/f606/coach-messages', 'رسائل الكوتش', 'Coach Messages', { linked: true }),
        P('/control/f606/ratings', 'التقييمات', 'Ratings', { linked: true }),
        P('/control/f606/follows', 'المتابعات', 'Follows', { linked: true }),
        P('/control/f606/food-analyses', 'تحليل الغذاء', 'Food Analyses', { linked: true }),
        P('/control/f606/weight-logs', 'سجلات الوزن', 'Weight Logs', { linked: true }),
        P('/control/f606/webhook-logs', 'سجل Webhooks', 'Webhook Logs', { linked: true }),
        P('/control/f606/actions', 'الإجراءات (تأكيد/شحن/حظر/هدايا/بث)', 'Actions'),
      ] }),
    ],
  },
  { key: 'sup.606-community', ar: '606 COMMUNITY (الإشراف)', en: '606 COMMUNITY (Supervision)', group: 'supervision', comingSoon: true, pages: [] },
  { key: 'sup.606-mediadocs', ar: '606 MEDIADOCS (الإشراف)', en: '606 MEDIADOCS (Supervision)', group: 'supervision', comingSoon: true, pages: [] },
  {
    key: 'sup.asb-admin', ar: 'ASB606 ADMIN (الإشراف)', en: 'ASB606 ADMIN (Supervision)', group: 'supervision',
    pages: [
      P('/control/asb606-admin/admins', 'إدارة المشرفين', 'Admin Management'),
      P('/control/asb606-admin/permissions', 'صلاحيات المشرفين والأدمن', 'Admin & Supervisor Permissions'),
      P('/control/asb606-admin/security', 'الأمان والحماية', 'Security & Protection'),
      P('/control/asb606-admin/company-docs', 'وثائق الشركة', 'Company Documents', { children: [
        P('/control/asb606-admin/partner-code', 'كود الشريك', 'Partner Code', { linked: true }),
        P('/control/asb606-admin/bank-account', 'الحساب البنكي', 'Bank Account', { linked: true }),
        P('/control/asb606-admin/expiry-watch', 'متابعة انتهاء الوثائق', 'Expiry Watch', { linked: true }),
      ] }),
      P('/control/asb606-admin/app-versions', 'التحكم بتطبيق الأدمن', 'Admin App Control'),
    ],
  },
  {
    key: 'sup.mt5', ar: '606 MT5 AI AND MANUAL TRADING', en: '606 MT5 Trading', group: 'supervision',
    pages: [
      P('/meta5-trading-hub', 'مركز تداول MT5', 'MT5 Trading Hub', { children: [
        P('/meta5-trading/ai', 'التداول بالذكاء الاصطناعي', 'AI Trading'),
        P('/meta5-trading/manual', 'التداول اليدوي', 'Manual Trading', { children: [
          P('/meta5-trading/manual/log', 'سجل التداول اليدوي', 'Manual Trading Log'),
        ] }),
      ] }),
    ],
  },
  {
    key: 'sup.support', ar: 'الدعم', en: 'Support', group: 'supervision',
    pages: [
      P('/support/customers', 'مركز الدعم (واتساب)', 'Support Center (WhatsApp)'),
      P('/support/audit', 'تدقيق الدعم', 'Support Audit'),
    ],
  },

  // ───────────── أزرار الأقسام (الصف السفلي في الصفحة الرئيسية) ─────────────
  {
    key: 'marketing', ar: 'التسويق', en: 'Marketing', group: 'sections',
    pages: [
      P('/marketing', 'مركز التسويق والإعلانات', 'Marketing & Ads Center', { children: [
        P('/marketing/:categoryId', 'فئة تسويقية', 'Marketing Category', { linked: true }),
        P('/marketing/meta-app-review', 'مراجعة تطبيق Meta', 'Meta App Review', { children: [
          P('/marketing/meta-app-review/:capId/calls', 'سجل نداءات الصلاحية', 'Capability Calls', { linked: true }),
        ] }),
      ] }),
    ],
  },
  {
    key: 'finance', ar: 'المالية', en: 'Finance', group: 'sections',
    pages: [
      P('/dashboard/finance', 'لوحة المالية', 'Finance Dashboard', { children: [
        P('/finance/cloudgm', 'CLOUDGM FZ LLC (المالية)', 'CLOUDGM FZ LLC (Finance)', { children: [
          P('/dashboard/finance/cloudgm/customers', 'منصة العملاء (المالية)', 'Customer Platform (Finance)', { children: [
            P('/dashboard/finance/cloudgm/customers/management', 'الإدارة المالية', 'Financial Management'),
            P('/dashboard/finance/cloudgm/customers/loyalty', 'التحكم بنقاط الولاء', 'Loyalty Points Control'),
            P('/dashboard/finance/cloudgm/customers/materials', 'الموارد المالية', 'Financial Resources'),
            P('/dashboard/finance/create-invoice/:platform', 'إنشاء فاتورة', 'Create Invoice', { linked: true }),
            P('/dashboard/finance/cloudgm/customers/payments', 'سجل المدفوعات الخاص بمنصة العملاء', 'Customer Payment Records'),
          ] }),
          P('/dashboard/finance/cloudgm/merchants', 'منصة التجار (المالية)', 'Merchant Platform (Finance)', { children: [
            P('/dashboard/finance/cloudgm/merchants/invoices', 'إنشاء الفواتير', 'Create Invoices'),
            P('/dashboard/finance/cloudgm/merchants/revenues', 'الإيرادات', 'Revenues', { children: [
              P('/dashboard/finance/cloudgm/merchants/commission-revenue', 'إيرادات العمولة', 'Commission Revenues'),
              P('/dashboard/finance/cloudgm/merchants/agent-payments', 'سجل مدفوعات فواتير الوكلاء', 'Agent Invoice Payments'),
              P('/dashboard/finance/cloudgm/merchants/tax-invoices', 'إيرادات نظام الوكلاء والمدفوعات الحكومية والعمولة', 'Agent System Revenue & Gov Fees'),
              P('/dashboard/finance/cloudgm/merchants/payments', 'إيرادات منصة العملاء للفواتير والخدمات', 'Customer Platform Revenue'),
              P('/dashboard/finance/cloudgm/merchants/store-revenues', 'إيرادات المتاجر', 'Store Revenues'),
            ] }),
            P('/dashboard/finance/cloudgm/merchants/withdrawal', 'السحب', 'Withdrawal', { children: [
              P('/dashboard/finance/cloudgm/merchants/insert-withdrawal-request', 'إدراج طلب سحب', 'Insert Withdrawal Request'),
              P('/dashboard/finance/cloudgm/merchants/withdrawal-requests', 'طلبات السحب', 'Withdrawal Requests', { children: [
                P('/dashboard/finance/cloudgm/merchants/withdrawal-requests/:id', 'تفاصيل طلب السحب', 'Withdrawal Request Detail', { linked: true }),
              ] }),
            ] }),
          ] }),
        ] }),
        P('/dashboard/finance/platform/:platform', 'مالية المنصات الأخرى (Fitness / Coin / Community / MediaDocs)', 'Other Platforms Finance', { linked: true, children: [
          P('/dashboard/finance/pcol/:platform', 'تحصيل المدفوعات', 'Payment Collection', { linked: true }),
        ] }),
        P('/finance/asb606', 'ASB606 ADMIN (المالية)', 'ASB606 ADMIN (Finance)', { children: [
          P('/finance/asb606/payments/entry', 'الإدراج المالي للشركة', 'Financial Entry', { children: [
            P('/finance/asb606/payments/salaries', 'الرواتب (إدراج/سجل)', 'Salaries', { linked: true }),
            P('/finance/asb606/payments/company', 'مدفوعات الشركة (إدراج/سجل)', 'Company Payments', { linked: true, children: [
              P('/finance/asb606/payments/company/add', 'إضافة دفعة', 'Add Payment', { linked: true }),
              P('/finance/asb606/payments/company/bulk-add', 'إضافة دفعات متعددة', 'Bulk Add Payments', { linked: true }),
            ] }),
            P('/finance/asb606/payments/assets', 'أصول الشركة (إدراج/سجل)', 'Company Assets', { linked: true }),
            P('/finance/asb606/payments/liabilities', 'مستحقات الشركة (إدراج/سجل)', 'Liabilities', { linked: true }),
          ] }),
          P('/finance/asb606/payments/records', 'السجل والمتابعة المالية للشركة', 'Financial Records & Tracking'),
        ] }),
        P('/dashboard/finance/vat', 'الضريبة المضافة', 'VAT', { children: [
          P('/dashboard/finance/vat/control', 'التحكم بالضريبة', 'Tax Control'),
          P('/dashboard/finance/vat/report', 'التقرير الضريبي', 'Tax Report'),
        ] }),
        P('/dashboard/finance/settings', 'إعدادات القسم المالي', 'Finance Settings', { children: [
          P('/dashboard/finance/settings/gateways', 'إعدادات بوابات الدفع', 'Payment Gateways'),
          P('/dashboard/finance/settings/commission', 'نسبة عمولة تابي وتمارا', 'Tabby & Tamara Commission'),
          P('/dashboard/finance/settings/dropdown-lists', 'القوائم المنسدلة (المالية)', 'Finance Dropdown Lists', { children: [
            P('/dashboard/finance/settings/dropdown-lists/withdrawal-rejection', 'أسباب رفض طلبات السحب', 'Withdrawal Rejection Reasons'),
            P('/dashboard/finance/settings/dropdown-lists/invoice', 'قوائم إنشاء الفاتورة (إصدار قديم)', 'Invoice Dropdowns (Legacy)'),
            P('/dashboard/finance/settings/dropdown-lists/invoice-hierarchical', 'قوائم إنشاء الفاتورة (رئيسي + فرعي)', 'Invoice Dropdowns (Main + Sub)'),
            P('/control-panel-settings/dropdowns/entity-types', 'أنواع الجهات', 'Entity Types'),
            P('/dashboard/finance/settings/payment-entities', 'جهات الدفع وأسعار الصرف', 'Payment Entities & Rates'),
          ] }),
          P('/dashboard/finance/settings/platform-urls', 'روابط المنصات', 'Platform URLs'),
          P('/dashboard/finance/settings/receipt-studio', 'ستوديو الإيصالات', 'Receipt Studio'),
          P('/dashboard/finance/settings/ecommerce-commission', 'عمولة المتاجر الإلكترونية', 'E-commerce Commission'),
          P('/dashboard/finance/settings/customer-ads-pricing', 'التحكم برسوم إعلانات منصة العملاء', 'Customer Ads Fees'),
        ] }),
        P('/dashboard/finance/statistics', 'الإحصائيات المالية', 'Financial Statistics'),
      ] }),
    ],
  },
  { key: 'platform-supervision', ar: 'متابعة مؤشر الأداء', en: 'Performance Monitoring', group: 'sections', comingSoon: true, pages: [] },
  {
    key: 'platform-control', ar: 'التحكم والمعلومات', en: 'Control & Info', group: 'sections',
    pages: [
      P('/control/cloudgm', 'التحكم والمعلومات (منصة العملاء)', 'Control & Info (Customers)', { children: [
        P('/control/cloudgm#gov-services-packages', 'باقات تخليص الخدمات الحكومية', 'Government Services Packages', { linked: true, caps: { edit: true, delete: true } }),
        P('/control/cloudgm#printing-documents', 'طباعة المستندات', 'Documents Printing', { linked: true, caps: { edit: true, delete: true } }),
        P('/control/company-setup', 'تأسيس الشركات', 'Company Setup', { children: [
          P('/control/company-setup/database', 'قاعدة بيانات الرخص التجارية والأوراق', 'Licenses & Documents Database', { children: [
            P('/control/company-setup/database/uae', 'الإمارات — الجهات وقواعد الرخص', 'UAE Authorities', { linked: true, children: [
              P('/control/company-setup/ras-al-khaimah', 'إمارة رأس الخيمة', 'Ras Al Khaimah', { linked: true, children: [
                P('/control/company-setup/ras-al-khaimah/rakez', 'RAKEZ', 'RAKEZ', { linked: true, children: [
                  P('/control/company-setup/ras-al-khaimah/rakez/statistics', 'إحصائيات RAKEZ', 'RAKEZ Statistics', { linked: true }),
                  P('/control/company-setup/formation-packages', 'باقات التأسيس', 'Formation Packages', { linked: true }),
                  P('/control/cloudgm/master-documents', 'القائمة المركزية للأوراق', 'Master Documents List', { linked: true }),
                  P('/control/company-setup/formation-packages/new', 'باقة تأسيس جديدة', 'New Formation Package', { linked: true }),
                  P('/control/company-setup/formation-packages/:id/edit', 'تعديل باقة تأسيس', 'Edit Formation Package', { linked: true }),
                ] }),
                P('/control/company-setup/ras-al-khaimah/ded', 'دائرة التنمية الاقتصادية (DED)', 'DED', { linked: true }),
              ] }),
            ] }),
          ] }),
          P('/control/company-setup/inputs', 'قائمة مدخلات تسجيل الشركات', 'Company Registration Inputs'),
        ] }),
        P('/control/cloudgm/customer-dropdowns', 'القوائم المنسدلة للتحكم بمنصة العملاء', 'Customer Platform Dropdown Lists', { children: [
          P('/control/cloudgm/customer-dropdowns/manual-entry', 'مسمى الخدمة الرئيسية والفرعية لمنصة العملاء', 'Main & Sub Service Names'),
          P('/control/cloudgm/customer-dropdowns/ban-reasons', 'أسباب حظر الحساب (منصة العملاء)', 'Customer Ban Reasons'),
          P('/control/cloudgm/customer-dropdowns/phone-operators', 'قوائم مشغّلي أرقام الهواتف', 'Phone Operators Lists'),
          P('/control/cloudgm/customer-dropdowns/partner-offices', 'قائمة مكاتب الشركاء', 'Partner Offices List'),
        ] }),
        P('/control/cloudgm/search-logs', 'سجلات محركات البحث', 'Search Engine Logs'),
        P('/visa-requirements', 'التأشيرة السياحية', 'Tourist Visa'),
        P('/control/cloudgm/legal-translation', 'الترجمة القانونية', 'Legal Translation', { children: [
          P('/control/cloudgm/legal-translation/:serviceId', 'تفاصيل خدمة ترجمة', 'Translation Service Detail', { linked: true }),
        ] }),
        P('/control/cloudgm/tax-services', 'الخدمات الضريبية', 'Tax Services', { children: [
          P('/control/cloudgm/tax-services/:serviceId', 'تفاصيل خدمة ضريبية', 'Tax Service Detail', { linked: true }),
        ] }),
        P('/control/cloudgm/visa-id', 'طلب التأشيرة والهوية', 'Visa & ID Application', { children: [
          P('/control/cloudgm/visa-id/:serviceId', 'تفاصيل خدمة تأشيرة/هوية', 'Visa/ID Service Detail', { linked: true }),
        ] }),
        P('/control/cloudgm/document-auth', 'توثيق المستندات', 'Document Authentication', { children: [
          P('/control/cloudgm/document-auth/:serviceId', 'تفاصيل خدمة توثيق', 'Document Auth Detail', { linked: true }),
        ] }),
        P('/control/cloudgm/traffic-services', 'المرور والترخيص', 'Traffic & Licensing', { children: [
          P('/control/cloudgm/traffic-services/:serviceId', 'تفاصيل خدمة مرورية', 'Traffic Service Detail', { linked: true }),
        ] }),
      ] }),
      P('/control/cloudgm-business', 'التحكم والمعلومات (منصة التجار)', 'Control & Info (Merchants)', { children: [
        P('/control/cloudgm-business#dropdown-lists', 'القوائم المنسدلة (التجار)', 'Dropdown Lists (Merchants)', { linked: true, caps: { edit: true, delete: true } }),
        P('/control/cloudgm-business/categories', 'إدارة تصنيفات المتاجر', 'Store Categories Management'),
        P('/control/cloudgm-business/store-types', 'التحكم بأنواع المتاجر', 'Store Types Control'),
        P('/integrations/merchant-platform', 'مزامنة منصة التجار', 'Merchant Platform Sync'),
        P('/control/cloudgm-business/plate-codes', 'التحكم بأكواد لوحات السيارات', 'Plate Codes Control', { children: [
          P('/control/cloudgm-business/plate-codes/:emirate/:category', 'أكواد إمارة/فئة', 'Emirate/Category Codes', { linked: true }),
        ] }),
        P('/control/cloudgm-business/agent-cooldown', 'التحكم بمدة تقديم طلب الوكيل', 'Agent Reapply Cooldown'),
        P('/control/cloudgm-business/merchant-dropdowns', 'مسمى الخدمة لإدخال المهام (يدوي)', 'Manual Task Entry Service Name'),
      ] }),
      P('/control/cloudgm-fzlc-management', 'إدارة الشركة CLOUDGM FZ-LLC (التحكم)', 'Manage CLOUDGM FZ-LLC (Control)', { children: [
        P('/control/cloudgm-fzlc-management/dropdowns', 'القوائم المنسدلة لإدارة الشركة', 'Company Management Dropdown Lists', { children: [
          P('/control/cloudgm-fzlc-management/company-services', 'قوائم خدمات الشركات لملفات الشركات', 'Company Services Lists'),
        ] }),
      ] }),
      P('/dashboard/cgm-coin', 'CGM COIN (التحكم)', 'CGM COIN (Control)'),
      P('/dashboard/606-fitness', '606 FITNESS (التحكم)', '606 FITNESS (Control)', { children: [
        P('/dashboard/606-fitness/members', 'الأعضاء', 'Members', { linked: true }),
        P('/dashboard/606-fitness/training', 'سجلات التمارين', 'Training Logs', { linked: true }),
        P('/dashboard/606-fitness/nutrition', 'سجلات التغذية', 'Nutrition Logs', { linked: true }),
        P('/dashboard/606-fitness/orders', 'الطلبات', 'Orders', { linked: true }),
        P('/dashboard/606-fitness/store', 'المتجر', 'Store', { linked: true }),
        P('/dashboard/606-fitness/:sectionId', 'أقسام أخرى', 'Other Sections', { linked: true }),
      ] }),
      P('/dashboard/606-community', '606 COMMUNITY (التحكم)', '606 COMMUNITY (Control)'),
      P('/dashboard/606-mediadocs', '606 MEDIADOCS (التحكم)', '606 MEDIADOCS (Control)'),
      P('/control-panel-settings', 'ASB606 ADMIN (التحكم)', 'ASB606 ADMIN (Control)', { children: [
        P('/control/asb606-admin/master-documents', 'قائمة الأوراق المطلوبة', 'Required Documents List'),
        P('/control/asb606-admin/master-violations', 'قائمة مخالفات الخدمات', 'Service Violations List'),
        P('/control-panel-settings/dropdowns', 'القوائم المنسدلة', 'Dropdown Lists', { children: [
          P('/control-panel-settings/dropdowns/job-titles', 'المسميات الوظيفية', 'Job Titles'),
        ] }),
        P('/control-panel-settings/image-library', 'رفع الصور', 'Image Library'),
        P('/control/files', 'رفع الملفات', 'File Uploads'),
        P('/control-panel-settings/company-mgmt', 'التحكم بإدارة الشركة', 'Company Management Control', { children: [
          P('/control-panel-settings/company-mgmt/certificates', 'شهادات لمن يهمه الأمر والتهنئة (مواضيع)', 'Certificate Subjects'),
          P('/control-panel-settings/company-mgmt/branding', 'ختم ومسمى الشركة', 'Company Stamp & Name', { children: [
            P('/control-panel-settings/company-mgmt/branding/name', 'مسمى الشركة', 'Company Name'),
            P('/control-panel-settings/company-mgmt/employees/stamp', 'ختم الشركة', 'Company Stamp'),
          ] }),
          P('/control-panel-settings/company-mgmt/employees', 'إعدادات الموظفين', 'Employees Settings', { children: [
            P('/control-panel-settings/company-mgmt/employees/signature', 'التوقيعات', 'Signatures', { children: [
              P('/control-panel-settings/company-mgmt/employees/signature/new', 'توقيع جديد', 'New Signature'),
              P('/control-panel-settings/company-mgmt/employees/signature/:userId', 'تعديل توقيع موظف', 'Edit Signature', { linked: true }),
            ] }),
          ] }),
        ] }),
      ] }),
      P('/control/support', 'الدعم (التحكم)', 'Support (Control)'),
      P('/qr-codes', 'نظام QR CODE', 'QR Code System', { children: [
        P('/qr-codes/new', 'إنشاء QR Code جديد', 'Create New QR Code'),
        P('/qr-codes/manage', 'متابعة والتحكم في الـ QR Code', 'Manage QR Codes'),
        P('/qr-codes/stats', 'الإحصائيات', 'Statistics'),
      ] }),
      P('/control/mobile-apps', 'التحكم بتطبيقات الهاتف', 'Mobile Apps Control'),
      P('/control/shared-assets', 'قاعدة صور المنصات', 'Shared Assets Library'),
    ],
  },
  {
    key: 'archive', ar: 'الأرشيف', en: 'Archive', group: 'sections',
    pages: [
      P('/control/archive', 'الأرشيف', 'Archive', { children: [
        P('/control/archive/asb606-admin', 'أرشيف ASB606 ADMIN', 'ASB606 ADMIN Archive', { children: [
          P('/control/archive/asb606-admin/supervisors', 'أرشيف المشرفين والموظفين', 'Supervisors & Staff Archive'),
          P('/control/archive/asb606-admin/admins', 'أرشيف حسابات الإدارة', 'Admin Accounts Archive'),
        ] }),
        P('/control/archive/cloudgm', 'أرشيف CLOUDGM (العملاء)', 'CLOUDGM Archive', { children: [
          P('/control/archive/cloudgm/accounts', 'الحسابات المؤرشفة', 'Archived Accounts'),
        ] }),
        P('/control/archive/cloudgm-business', 'أرشيف CLOUDGM Business', 'CLOUDGM Business Archive', { children: [
          P('/control/archive/cloudgm-business/:variant', 'أرشيف المتاجر (شخصية/شركات)', 'Stores Archive', { linked: true }),
          P('/control/archive/cloudgm-business/accounts', 'الحسابات المؤرشفة', 'Archived Accounts'),
        ] }),
        P('/control/archive/606-fitness', 'أرشيف 606 FITNESS', '606 FITNESS Archive'),
        P('/control/archive/ai-chats', 'أرشيف محادثات الذكاء الاصطناعي', 'AI Chats Archive'),
      ] }),
    ],
  },
  {
    key: 'ai', ar: 'Cloud GM AI Control Hub', en: 'Cloud GM AI Control Hub', group: 'sections',
    pages: [
      P('/control/ai', 'Cloud GM AI Control Hub', 'Cloud GM AI Control Hub', { children: [
        P('/control/ai/models/:agentCode', 'صفحة بوت / نموذج', 'Bot / Model Detail', { linked: true }),
        P('/control/ai/moderation', 'مراقبة المحادثات', 'Chat Moderation'),
      ] }),
    ],
  },
];

/* Pages everyone logged-in can use (staff self-service) — never gated. */
export const ALWAYS_ALLOWED = ['/dashboard', '/admin-dashboard', '/employee-dashboard', '/account-settings', '/messages',
  '/tasks-hub', '/tasks', '/tasks/delegated', '/signatures-approvals', '/signatures-approvals/certificates', '/signatures-approvals/settings'];

// ───────────────────────── derived indexes ─────────────────────────
const genPages = generated.pages || {};
const genRoutes = new Set(generated.routes || []);
const genReachable = new Set(generated.reachable || []);

function routeOf(id) { return id.split('#')[0]; }
const toRx = (pattern) => new RegExp('^' + pattern.split('/').map((s) => (s.startsWith(':') ? '[^/]+' : s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))).join('/') + '$');
const genRouteRx = [...genRoutes].filter((r) => r.includes(':')).map(toRx);
const genReachableRx = [...genReachable].filter((r) => r.includes(':')).map(toRx);
const known = (set, rxs, route) => set.has(route) || rxs.some((rx) => rx.test(route));

export const PAGE_INDEX = {};   // id → { ...node, sectionKey, depth, exists, linked, caps }
export const SECTION_OF_PAGE = {};
const genTabs = generated.tabs || {};
const walk = (node, sectionKey, depth, parentId) => {
  const route = routeOf(node.id);
  const tabId = node.id.includes('#') ? node.id.split('#')[1] : null;
  const g = (tabId ? genTabs[tabId] : genPages[route]) || {};
  const info = {
    ...node, sectionKey, depth, parentId, route,
    exists: known(genRoutes, genRouteRx, route),
    linked: Boolean(node.linked) || known(genReachable, genReachableRx, route),
    caps: node.caps || g.caps || { edit: false, delete: false },
    file: g.file || null,
  };
  PAGE_INDEX[node.id] = info;
  SECTION_OF_PAGE[node.id] = sectionKey;
  (node.children || []).forEach((c) => walk(c, sectionKey, depth + 1, node.id));
};
SECTIONS.forEach((s) => s.pages.forEach((p) => walk(p, s.key, 0, null)));

/* Routes that exist in App.js but are NOT in the curated registry (orphans / legacy / new pages). */
const PUBLIC_ROUTES = new Set(['/', '/xK9mP2vL8qR4wN6j', '/reset-password', '/set-password', '/pay/receipt/:invoiceId', '/payment/cancel', '/payment/success', '*', ...ALWAYS_ALLOWED]);
const registeredRoutes = new Set(Object.values(PAGE_INDEX).map((p) => p.route));
export const UNREGISTERED_ROUTES = (generated.routes || [])
  .filter((r) => !registeredRoutes.has(r) && !PUBLIC_ROUTES.has(r))
  .map((r) => ({ id: r, file: genPages[r]?.file || null, component: genPages[r]?.component || null, caps: genPages[r]?.caps || {}, linked: genReachable.has(r) }));

/* Flatten a section to ordered rows (depth-first) for the grid. */
export const flattenSection = (section) => {
  const rows = [];
  const rec = (n) => { rows.push(PAGE_INDEX[n.id]); (n.children || []).forEach(rec); };
  section.pages.forEach(rec);
  return rows;
};

/* Match a live location (pathname + ?tab=) to a registry page id. */
const patternToRegex = toRx;
const ROUTE_PATTERNS = Object.values(PAGE_INDEX).filter((p) => !p.id.includes('#'))
  .map((p) => ({ id: p.id, rx: patternToRegex(p.route), isDynamic: p.route.includes(':') }))
  .sort((a, b) => Number(a.isDynamic) - Number(b.isDynamic)); // static first
export function matchPage(pathname, search = '') {
  const tab = new URLSearchParams(search).get('tab');
  if (tab && PAGE_INDEX[`${pathname}#${tab}`]) return PAGE_INDEX[`${pathname}#${tab}`];
  const hit = ROUTE_PATTERNS.find((r) => r.rx.test(pathname));
  return hit ? PAGE_INDEX[hit.id] : null;
}
export const generatedAt = generated.generated_at;
